import React, { useState } from 'react';
import { Button, Form, Modal, Icon } from 'semantic-ui-react';
import '@aws-amplify/ui-react/styles.css';

import 'react-datepicker/dist/react-datepicker.css';
import { confirmResetPassword } from 'aws-amplify/auth';
import Input from './Input';

interface ConfirmPasswordResetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  // `username` is the user's email address. Amplify's confirmResetPassword API
  // takes a `username` field, but in both Cognito pool configs (old:
  // AliasAttributes:["email"], new: UsernameAttributes:["email"]) the email is
  // what's passed in. Prop name kept as `username` to match the Amplify API.
  username: string;
}

const ConfirmPasswordReset: React.FC<ConfirmPasswordResetProps> = ({
  isOpen,
  onClose,
  onSubmit,
  username,
}) => {
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Please enter a valid 6-digit code');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords don't match");
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmResetPassword({
        username,
        confirmationCode: otpCode,
        newPassword,
      });

      setOtpCode('');
      setNewPassword('');
      setConfirmPassword('');
      setOtpError('');
      setPasswordError('');

      onSubmit();
    } catch (error: any) {
      console.error('Error confirming password reset:', error);
      setOtpError(error.message || 'Failed to confirm password reset. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setOtpCode('');
    setNewPassword('');
    setConfirmPassword('');
    setOtpError('');
    setPasswordError('');
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleCancel} size="small">
      <Modal.Header>Enter Verification Code</Modal.Header>
      <Modal.Content>
        <p>
          A verification code has been sent to your email address. Please enter the code and a new
          password.
        </p>
        <Form autoComplete="off">
          <Form.Field>
            <Input
              placeholder="6-digit Verification Code"
              value={otpCode}
              onChange={(value: string) => setOtpCode(value)}
              error={otpError ? otpError : null}
              maxLength={6}
              pattern={'[0-9]*' as '[0-9]*'}
              autoComplete="one-time-code"
            />
          </Form.Field>
          <Form.Field>
            <div style={{ position: 'relative' }}>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="New Password"
                value={newPassword}
                onChange={(value: string) => setNewPassword(value)}
                error={passwordError || null}
                autoComplete="new-password"
              />
              <Icon
                name={showPassword ? 'eye slash' : 'eye'}
                link
                style={{ position: 'absolute', top: '40%', right: 10 }}
                onClick={() => setShowPassword(!showPassword)}
              />
            </div>
          </Form.Field>
          <Form.Field>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Re-enter Password"
              value={confirmPassword}
              onChange={(value: string) => setConfirmPassword(value)}
              error={passwordError || null}
              autoComplete="new-password"
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          primary
          loading={isSubmitting}
          disabled={!otpCode || otpCode.length !== 6 || !newPassword || !confirmPassword}
        >
          Submit
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ConfirmPasswordReset;
